import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Users, Calendar, Download, Mail, Search, Loader2, TrendingUp, Globe,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LeadsManager = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');  // 'all' | 'event' | 'direct' | eventId
  const [search, setSearch] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const statsRes = await fetch(`${API_URL}/api/admin/leads/stats`, { headers });
      if (statsRes.ok) setStats(await statsRes.json());

      const qs = new URLSearchParams();
      if (filter === 'event' || filter === 'direct') qs.set('source', filter);
      else if (filter !== 'all') qs.set('event_id', filter);
      qs.set('limit', '2000');

      const res = await fetch(`${API_URL}/api/admin/leads?${qs}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setLeads(data.leads || []);
      } else {
        toast.error('Failed to load leads');
      }
    } catch {
      toast.error('Failed to load leads');
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, token]);

  useEffect(() => { load(); }, [load]);

  const exportCSV = async () => {
    try {
      const qs = new URLSearchParams();
      if (filter === 'event' || filter === 'direct') qs.set('source', filter);
      else if (filter !== 'all') qs.set('event_id', filter);

      const res = await fetch(`${API_URL}/api/admin/leads/export?${qs}`, { headers });
      if (!res.ok) return toast.error('Export failed');

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.headers.get('content-disposition')?.match(/filename="?([^"]+)"?/)?.[1] || 'leads.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch {
      toast.error('Export failed');
    }
  };

  // Client-side search filter
  const filtered = leads.filter(l => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (l.email || '').toLowerCase().includes(q) ||
           (l.name || '').toLowerCase().includes(q) ||
           (l.source_event_name || '').toLowerCase().includes(q);
  });

  return (
    <div data-testid="leads-manager" className="space-y-4">
      {/* Top Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
          <Card className="bg-rooam-gray-medium border-rooam-gray-light" data-testid="stat-total-leads">
            <CardContent className="p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <Users className="w-3.5 h-3.5 text-cyan-400" />
                <span className="text-zinc-500 text-[10px] font-medium">Total Users</span>
              </div>
              <span className="text-white font-black text-xl">{stats.total_users}</span>
            </CardContent>
          </Card>
          <Card className="bg-rooam-gray-medium border-rooam-gray-light" data-testid="stat-event-leads">
            <CardContent className="p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <Calendar className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-zinc-500 text-[10px] font-medium">From Events</span>
              </div>
              <span className="text-white font-black text-xl">{stats.event_leads}</span>
            </CardContent>
          </Card>
          <Card className="bg-rooam-gray-medium border-rooam-gray-light" data-testid="stat-direct-leads">
            <CardContent className="p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <Globe className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-zinc-500 text-[10px] font-medium">Direct</span>
              </div>
              <span className="text-white font-black text-xl">{stats.direct_leads}</span>
            </CardContent>
          </Card>
          <Card className="bg-rooam-gray-medium border-rooam-gray-light" data-testid="stat-conversion">
            <CardContent className="p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <TrendingUp className="w-3.5 h-3.5 text-red-400" />
                <span className="text-zinc-500 text-[10px] font-medium">Event %</span>
              </div>
              <span className="text-white font-black text-xl">
                {stats.total_users > 0 ? Math.round((stats.event_leads / stats.total_users) * 100) : 0}%
              </span>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Source breakdown */}
      {stats && stats.breakdown?.length > 0 && (
        <Card className="bg-rooam-gray-medium border-rooam-gray-light">
          <CardContent className="p-3">
            <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-2">Signup Sources</p>
            <div className="flex flex-wrap gap-1.5">
              {stats.breakdown.slice(0, 10).map(b => (
                <button
                  key={b.source}
                  onClick={() => {
                    if (b.source.startsWith('event:')) setFilter(b.source.split(':', 2)[1]);
                    else setFilter(b.source);
                  }}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    (filter === b.source.split(':')[1] || filter === b.source)
                      ? 'bg-rooam-red border-rooam-red text-white'
                      : 'bg-rooam-black border-zinc-700 text-zinc-300 hover:border-cyan-500/50'
                  }`}
                  data-testid={`source-filter-${b.source}`}
                >
                  {b.label} <span className="text-zinc-500 ml-1">({b.count})</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[140px] max-w-[260px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500 pointer-events-none" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search name/email..."
            className="bg-rooam-gray-medium border-rooam-gray-light text-white text-xs pl-7"
            data-testid="leads-search-input"
          />
        </div>
        <select
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className="bg-rooam-gray-medium border border-zinc-700 text-zinc-300 text-xs rounded-md px-2 py-2"
          data-testid="leads-source-filter"
        >
          <option value="all">All Sources</option>
          <option value="event">Event Signups Only</option>
          <option value="direct">Direct Signups Only</option>
        </select>
        <Button
          onClick={exportCSV}
          className="text-xs gap-1.5 bg-emerald-600 hover:bg-emerald-700 ml-auto shrink-0"
          data-testid="export-leads-csv"
        >
          <Download className="w-3.5 h-3.5" /> Export CSV
        </Button>
      </div>

      {/* Leads List */}
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-zinc-600 text-sm text-center py-8">No leads found</p>
      ) : (
        <div className="space-y-1.5">
          <p className="text-zinc-500 text-[10px] uppercase tracking-widest">Showing {filtered.length} of {leads.length}</p>
          {filtered.map(lead => (
            <Card key={lead.id} className="bg-rooam-gray-medium border-rooam-gray-light" data-testid={`lead-row-${lead.id}`}>
              <CardContent className="p-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-semibold text-sm">{lead.name || '(no name)'}</span>
                      <Badge variant="outline" className="text-[8px] px-1.5 py-0 text-zinc-400 border-zinc-700 uppercase">{lead.role}</Badge>
                      {lead.source_type === 'event' && (
                        <Badge className="text-[8px] px-1.5 py-0 bg-emerald-500/20 text-emerald-300 border-emerald-700/40 uppercase font-bold" data-testid={`lead-source-event-${lead.id}`}>
                          {lead.source_event_name}
                        </Badge>
                      )}
                      {lead.source_type === 'direct' && (
                        <Badge className="text-[8px] px-1.5 py-0 bg-zinc-700/40 text-zinc-400 border-zinc-600 uppercase">Direct</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 flex-wrap text-[11px] text-zinc-500">
                      <a href={`mailto:${lead.email}`} className="flex items-center gap-1 text-cyan-400 hover:text-cyan-300">
                        <Mail className="w-3 h-3" />{lead.email}
                      </a>
                      <span>{lead.created_at ? new Date(lead.created_at).toLocaleDateString() : '—'}</span>
                      {lead.subscription_status && (
                        <Badge variant="outline" className={`text-[8px] px-1.5 py-0 ${
                          lead.subscription_status === 'active' ? 'text-emerald-400 border-emerald-800' :
                          lead.subscription_status === 'pending_payment' ? 'text-amber-400 border-amber-800' :
                          'text-zinc-500 border-zinc-700'
                        }`}>
                          {lead.subscription_status.replace('_', ' ')}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default LeadsManager;
